import { useState } from "react"
import { ArrowLeft, Loader2 } from "lucide-react"

import ConfirmStar from "@/assets/img/soulmate/confirm-star.png"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import type { StepComponentProps } from "./step-contract"

/**
 * Full-bleed email opt-in confirmation (Figma Email → Confirm): a top glow, a
 * glowing star, title + description, and a primary "Confirm" / secondary
 * "No, thanks" pair — both advance to the next step (the choice only affects
 * opt-in, stored via the answer). `submitDelayMs` shows a brief loader on
 * Confirm, per the design's submitting state.
 */
export function SoulmateConfirmStep({ step, onChange, onAdvance, onBack }: StepComponentProps) {
  const [submitting, setSubmitting] = useState(false)

  const advance = (value: string) => {
    onChange(value)
    onAdvance()
  }

  const confirm = () => {
    if (submitting) return
    trackEvent(ANALYTICS_EVENTS.SOULMATE_QUIZ_CONFIRM, { metadata: { option: "yes" } })
    onChange("confirmed")
    if (step.submitDelayMs) {
      setSubmitting(true)
      setTimeout(onAdvance, step.submitDelayMs)
    } else {
      onAdvance()
    }
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light">
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-6 px-4 pt-5">
          <img
            src={ConfirmStar}
            alt=""
            className="h-28 w-[152px] object-cover mix-blend-lighten"
          />
          <div className="space-y-2 text-center">
            <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
            {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
          </div>
        </div>

        <div className="mt-auto space-y-2 px-4 pb-safe">
          {submitting ? (
            <div
              aria-busy="true"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light/70"
            >
              <Loader2 className="size-6 animate-spin" />
              Confirm
            </div>
          ) : (
            <Button variant="gradient" size="xl" onClick={confirm}>
              Confirm
            </Button>
          )}
          <Button
            variant="ghost"
            size="xl"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.SOULMATE_QUIZ_CONFIRM, { metadata: { option: "no" } })
              advance(step.secondaryAction?.value ?? "declined")
            }}
            className="bg-purple-light/10 text-purple-light hover:bg-purple-light/15"
          >
            {step.secondaryAction?.label ?? "No, thanks"}
          </Button>
        </div>
      </div>
    </div>
  )
}
